import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UserRole } from "@/types";
import { faviconBadge } from "@/lib/favicon-badge";

interface PingState {
  // UI State
  selectedChatId: string | null;
  isChatListOpen: boolean;

  // Chat management
  activeChatIds: string[]; // Chats currently being viewed/managed

  // User context
  currentUserRole: UserRole | null;

  // Filters and search
  chatListFilters: {
    status?: "open" | "assigned" | "resolved" | "closed";
    patient_name?: string;
    assigned_to_me?: boolean;
  };

  // UI preferences
  messageRefreshInterval: number; // in milliseconds
  showUnreadOnly: boolean;

  // Unread counts per tab
  patientUnreadCount: number;
  teamsUnreadCount: number;
  lastCheckedPatientTab: number | null; // timestamp when patient tab was last opened
  lastCheckedTeamsTab: number | null; // timestamp when teams tab was last opened
}

interface PingActions {
  // Chat selection
  setSelectedChat: (chatId: string | null) => void;
  clearSelectedChat: () => void;

  // Chat list management
  toggleChatList: () => void;
  setChatListOpen: (isOpen: boolean) => void;

  // Active chats management
  addActiveChat: (chatId: string) => void;
  removeActiveChat: (chatId: string) => void;
  clearActiveChats: () => void;

  // User role management
  setCurrentUserRole: (role: UserRole | null) => void;

  // Filter management
  setChatListFilters: (filters: Partial<PingState["chatListFilters"]>) => void;
  clearChatListFilters: () => void;

  // UI preferences
  setMessageRefreshInterval: (interval: number) => void;
  toggleShowUnreadOnly: () => void;
  setShowUnreadOnly: (showUnreadOnly: boolean) => void;

  // Unread count management per tab
  setPatientUnreadCount: (count: number) => void;
  setTeamsUnreadCount: (count: number) => void;
  markPatientTabAsChecked: () => void;
  markTeamsTabAsChecked: () => void;
  resetPatientUnreadCount: () => void;
  resetTeamsUnreadCount: () => void;

  // Utility actions
  resetPingState: () => void;
}

type PingStore = PingState & PingActions;

const initialState: PingState = {
  selectedChatId: null,
  isChatListOpen: true,
  activeChatIds: [],
  currentUserRole: null,
  chatListFilters: {},
  messageRefreshInterval: 30000, // 30 seconds default
  showUnreadOnly: false,
  patientUnreadCount: 0,
  teamsUnreadCount: 0,
  lastCheckedPatientTab: null,
  lastCheckedTeamsTab: null,
};

export const usePingStore = create<PingStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Chat selection actions
      setSelectedChat: (chatId) => {
        set({ selectedChatId: chatId });
        if (chatId) {
          get().addActiveChat(chatId);
        }
      },

      clearSelectedChat: () => set({ selectedChatId: null }),

      // Chat list actions
      toggleChatList: () =>
        set((state) => ({ isChatListOpen: !state.isChatListOpen })),
      setChatListOpen: (isOpen) => set({ isChatListOpen: isOpen }),

      // Active chats management
      addActiveChat: (chatId) =>
        set((state) => ({
          activeChatIds: state.activeChatIds.includes(chatId)
            ? state.activeChatIds
            : [...state.activeChatIds, chatId],
        })),

      removeActiveChat: (chatId) =>
        set((state) => ({
          activeChatIds: state.activeChatIds.filter((id) => id !== chatId),
          selectedChatId:
            state.selectedChatId === chatId ? null : state.selectedChatId,
        })),

      clearActiveChats: () => set({ activeChatIds: [], selectedChatId: null }),

      // User role management
      setCurrentUserRole: (role) => set({ currentUserRole: role }),

      // Filter management
      setChatListFilters: (filters) =>
        set((state) => ({
          chatListFilters: { ...state.chatListFilters, ...filters },
        })),

      clearChatListFilters: () => set({ chatListFilters: {} }),

      // UI preferences
      setMessageRefreshInterval: (interval) =>
        set({ messageRefreshInterval: interval }),

      toggleShowUnreadOnly: () =>
        set((state) => ({ showUnreadOnly: !state.showUnreadOnly })),

      setShowUnreadOnly: (showUnreadOnly) => set({ showUnreadOnly }),

      // Unread count management per tab
      setPatientUnreadCount: (count) => {
        set({ patientUnreadCount: count });
        // Update favicon badge with total unread count
        const totalUnread = count + get().teamsUnreadCount;
        faviconBadge.setBadgeCount(totalUnread);
      },
      setTeamsUnreadCount: (count) => {
        set({ teamsUnreadCount: count });
        // Update favicon badge with total unread count
        const totalUnread = count + get().patientUnreadCount;
        faviconBadge.setBadgeCount(totalUnread);
      },
      markPatientTabAsChecked: () => {
        set({
          lastCheckedPatientTab: Date.now(),
          patientUnreadCount: 0,
        });
        // Update favicon badge with remaining unread count
        const totalUnread = get().teamsUnreadCount;
        faviconBadge.setBadgeCount(totalUnread);
      },
      markTeamsTabAsChecked: () => {
        set({
          lastCheckedTeamsTab: Date.now(),
          teamsUnreadCount: 0,
        });
        // Update favicon badge with remaining unread count
        const totalUnread = get().patientUnreadCount;
        faviconBadge.setBadgeCount(totalUnread);
      },
      resetPatientUnreadCount: () => {
        set({ patientUnreadCount: 0 });
        // Update favicon badge with remaining unread count
        const totalUnread = get().teamsUnreadCount;
        faviconBadge.setBadgeCount(totalUnread);
      },
      resetTeamsUnreadCount: () => {
        set({ teamsUnreadCount: 0 });
        // Update favicon badge with remaining unread count
        const totalUnread = get().patientUnreadCount;
        faviconBadge.setBadgeCount(totalUnread);
      },

      // Utility actions
      resetPingState: () => {
        set(initialState);
        // Clear the favicon badge when resetting state
        faviconBadge.removeBadge();
      },
    }),
    {
      name: "ping-store",
      // Only persist essential UI preferences, not temporary state
      partialize: (state) => ({
        isChatListOpen: state.isChatListOpen,
        messageRefreshInterval: state.messageRefreshInterval,
        showUnreadOnly: state.showUnreadOnly,
        chatListFilters: state.chatListFilters,
        patientUnreadCount: state.patientUnreadCount,
        teamsUnreadCount: state.teamsUnreadCount,
        lastCheckedPatientTab: state.lastCheckedPatientTab,
        lastCheckedTeamsTab: state.lastCheckedTeamsTab,
        // Don't persist activeChatIds or currentUserRole as they should be fresh
      }),
      version: 3,
      migrate: (persistedState: any) => {
        if (!persistedState || typeof persistedState !== "object") {
          return persistedState;
        }

        const { selectedChatId: _selectedChatId, ...rest } = persistedState;
        return rest;
      },
    }
  )
);
